import Icon from '@ant-design/icons';
import { Button as AntdButton, type ButtonProps } from 'antd';
import { omit } from 'lodash/fp';
import styled from 'styled-components';
import RightIcon from '../../icons/mobility/rightIcon.svg';
import breakpoints from '../../utilities/breakpoints';
import themeTokens from './themeTokens';

const PorscheIcon = styled(RightIcon)`
    position: absolute;
    width: 1.5rem;
    height: 1.5rem;
    color: rgb(255, 255, 255);
    pointer-events: none;
    left: 0.6875rem;
    top: 0.6875rem;
    transform: translate3d(0px, 0px, 0px);
`;

const StyledButton = styled(AntdButton).withConfig({
    shouldForwardProp: prop =>
        !['porscheFallbackIcon', 'porscheFallbackIconSource', 'showIcon', 'isCompact'].includes(prop),
})<{ type?: string; showIcon?: boolean; isCompact?: boolean }>`
    &.ant-btn {
        height: 48px;
        min-width: fit-content;
        padding: 0.6875rem 0.9375rem;
        display: inline-flex;
        font-size: var(--button-font-size, 1rem);
        outline: transparent solid 1px;
        text-align: left;
        border-radius: 0;
        border: 1px solid var(--ant-primary-color);
        align-items: center;
        box-shadow: none;

        & span {
            pointer-events: none;
        }

        & .anticon {
            font-size: inherit;
        }

        &.ant-btn-primary:hover {
            background: ${themeTokens.hoveredPrimaryColor};
            border-color: ${themeTokens.hoveredPrimaryColor};
        }

        &.ant-btn-secondary {
            background: ${themeTokens.secondaryColor};
            border-color: ${themeTokens.secondaryColor};
            color: #fff;

            &:hover {
                color: white;
                background-color: ${themeTokens.hoveredSecondaryColor};
                border-color: ${themeTokens.hoveredSecondaryColor};

                & ${PorscheIcon} {
                    fill: white;
                }
            }
        }

        &.ant-btn-tertiary {
            background: white;
            border-color: ${themeTokens.tertiaryColor};
            color: ${themeTokens.textColor};

            & ${PorscheIcon} {
                fill: ${themeTokens.textColor};
            }

            &:hover {
                color: white;
                background-color: ${themeTokens.hoveredTertiaryColor};
                border-color: ${themeTokens.hoveredTertiaryColor};

                & ${PorscheIcon} {
                    fill: white;
                }
            }
        }

        &.ant-btn-link {
            border: none;
            width: fit-content;
            min-width: initial;
            border-color: transparent;
            color: ${themeTokens.linkColor};
            outline: none;

            ${({ isCompact }) =>
                isCompact &&
                `
                    padding: 0;
                    height: auto;
                `}

            &:hover {
                color: ${themeTokens.hoveredLinkColor};
            }

            ${({ showIcon }) =>
                !showIcon &&
                `
                & > .porsche-arrow {
                    display: none;
                }
            `}

            > svg {
                fill: currentColor;
                transition: fill 0.5s ease;
            }

            > svg + span {
                margin-left: 8px;
                margin-top: 2px;
            }

            :disabled,
            :hover:disabled {
                background-color: transparent;
                border-color: transparent;
                color: ${themeTokens.disabledColor};
            }
        }

        &:disabled,
        &:hover:disabled {
            background-color: ${themeTokens.disabledColor};
            border: 1px solid ${themeTokens.disabledColor};
            color: ${themeTokens.disabledTextColor};

            & ${PorscheIcon} {
                stroke: ${themeTokens.disabledTextColor};
                fill: ${themeTokens.disabledTextColor};
            }
        }

        & > .porsche-arrow {
            font-size: 1.5rem;
            width: 1.5rem;
            height: 1.5rem;
            transform: translateX(-0.25rem);
        }

        & > .porsche-arrow + span,
        & > span + .porsche-arrow {
            margin-left: 0px;
            line-height: 1.5rem;
            width: 100%;
        }

        &:hover > svg {
            fill: ${({ type = 'default' }) => (type === 'primary' ? '#fff' : 'var(--ant-primary-color)')};
        }

        &.ant-btn-icon-only {
            justify-content: center;
        }

        @media screen and (max-width: ${breakpoints.md}) {
            &.block-button {
                width: 100%;
                text-align: left;
            }
        }
    }
`;

const Button = ({
    children,
    type,
    icon = <Icon className="porsche-arrow" component={RightIcon} />,
    // Note, showIcon only applicable for link that still want to show the icon
    showIcon = false,
    isCompact,
    ...props
}: ButtonProps & { isCompact?: boolean; secondaryColor?: boolean; showIcon?: boolean }) => (
    <StyledButton
        icon={icon}
        isCompact={isCompact}
        showIcon={showIcon}
        type={type}
        {...omit(['porscheFallbackIcon', 'porscheFallbackIconSource'], props)}
    >
        {children}
    </StyledButton>
);

export default Button;
